import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import axios from 'axios';

import { PageLayout } from '../components/layout';
import { ActionButton, ConfirmationDialog } from '../components/common';
import {
  DIAGNOSIS_ALL_QUERY_KEY,
  useDiagnosisById,
  useDiagnosisCustomerById,
  useDiagnosisCustomerSitesById,
} from '../hooks';
import { diagnosisCloseExportApi } from '../services/apiClient';
import {
  DiagnosisCustomerSiteStep,
  DiagnosisDetailsStep,
  DiagnosisMetersStep,
  DiagnosisRelevantVariablesStep,
  DiagnosisIndicatorsStep,
  DiagnosisEnergyVectorsStep,
  DiagnosisCensimentiStep,
  DiagnosisGerarchieStep,
  DiagnosisConsumptionCostsStep,
  DiagnosisInterventionsStep,
  DiagnosisPodTimetablesStep,
  DiagnosisEnergyBalanceStep,
  DiagnosisMonitorEneaStep,
  DiagnosisEnergyUseStep,
  DiagnosisCloseExportStep,
} from '../components/diagnosis/steps';
import type { DiagnosisStepHandle } from '../components/diagnosis/steps/Step.types';
import { useAuth } from '../contexts/AuthContext';
import { DiagnosisPermissionProvider } from '../contexts/DiagnosisPermissionContext';
import { canEditDiagnosis } from '../utils/diagnosisPermissions';

const STEP_LABELS = [
  'Diagnosi',
  'Sito',
  'Vettori energetici',
  'Contatori',
  'Variabili pertinenti',
  'Consumi e costi',
  'Bilancio energetico',
  'Grafici POD orari',
  'Indicatori',
  'Usi energetici',
  'Censimenti',
  'Monitoraggio ENEA',
  'Interventi',
  'Gerarchia',
  'Chiudi ed esporta',
] as const;

type StepConfig = {
  label: string;
  actions: ('save' | 'add' | 'importData')[];
  actionLabels?: {
    save?: string;
    add?: string;
    importData?: string;
  };
  render: () => React.ReactNode;
};

export const DiagnosisDetailPage: React.FC = React.memo(() => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const diagnosisId = id ?? '';
  const { user } = useAuth();

  const {
    data: diagnosis,
    isLoading: loadingDiagnosis,
    error: diagnosisError,
  } = useDiagnosisById(diagnosisId);

  const {
    data: diagnosisSite,
    isLoading: loadingSite,
    error: diagnosisSiteError,
  } = useDiagnosisCustomerSitesById(diagnosis?.site_id ?? '');

  const {
    data: diagnosisCustomer,
    isLoading: loadingCustomer,
    error: diagnosisCustomerError,
  } = useDiagnosisCustomerById(diagnosisSite?.customer_id ?? '');

  const isLoading = loadingDiagnosis || loadingSite || loadingCustomer;
  const hasError = diagnosisError || diagnosisSiteError || diagnosisCustomerError;

  // Allinea a react-query: aggiorna quando cambiano i dati (es. dopo save/refetch), non solo l'id
  const memoizedDiagnosis = useMemo(() => diagnosis, [diagnosis]);
  const memoizedDiagnosisSite = useMemo(() => diagnosisSite, [diagnosisSite]);
  const memoizedDiagnosisCustomer = useMemo(() => diagnosisCustomer, [diagnosisCustomer]);

  const isReadOnly = useMemo(
    () => Boolean(memoizedDiagnosis && !canEditDiagnosis(user, memoizedDiagnosis)),
    [user, memoizedDiagnosis]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosingDiagnosis, setIsClosingDiagnosis] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const queryClient = useQueryClient();
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    stepIndex: number | null;
    targetStep: number | null;
  }>({
    open: false,
    stepIndex: null,
    targetStep: null,
  });

  const stepRefs = useRef(STEP_LABELS.map(() => React.createRef<DiagnosisStepHandle>())).current;

  const steps: StepConfig[] = useMemo(() => {
    // Early return se mancano dati essenziali
    if (
      !diagnosisId ||
      !memoizedDiagnosis ||
      !memoizedDiagnosisSite ||
      !memoizedDiagnosisCustomer
    ) {
      return [];
    }

    return [
      {
        label: 'Diagnosi',
        actions: ['save'],
        actionLabels: { save: 'Salva diagnosi' },
        render: () => (
          <DiagnosisDetailsStep
            ref={stepRefs[0]}
            diagnosisId={memoizedDiagnosis?.id}
            data={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Sito',
        actions: ['save'],
        actionLabels: { save: 'Salva cliente e sito' },
        render: () => (
          <DiagnosisCustomerSiteStep
            ref={stepRefs[1]}
            diagnosisCustomerId={memoizedDiagnosisCustomer?.id}
            diagnosisSiteId={memoizedDiagnosisSite?.id}
            diagnosisId={diagnosisId}
            customerData={memoizedDiagnosisCustomer}
            siteData={memoizedDiagnosisSite}
          />
        ),
      },
      {
        label: 'Vettori energetici',
        actions: ['add'],
        actionLabels: { add: 'Aggiungi vettore energetico' },
        render: () => <DiagnosisEnergyVectorsStep ref={stepRefs[2]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Contatori',
        actions: ['save', 'add'],
        actionLabels: {
          save: 'Salva dati contatori',
          add: 'Aggiungi contatore',
        },
        render: () => (
          <DiagnosisMetersStep
            ref={stepRefs[3]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Variabili pertinenti',
        actions: ['save', 'add'],
        actionLabels: {
          save: 'Salva dati variabili pertinenti',
          add: 'Aggiungi variabile pertinente',
        },
        render: () => (
          <DiagnosisRelevantVariablesStep
            ref={stepRefs[4]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Consumi e costi',
        actions: ['save'],
        actionLabels: { save: 'Salva consumi e costi' },
        render: () => (
          <DiagnosisConsumptionCostsStep
            ref={stepRefs[5]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Bilancio energetico',
        actions: [],
        render: () => (
          <DiagnosisEnergyBalanceStep
            ref={stepRefs[6]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Grafici POD orari',
        actions: ['save', 'add'],
        actionLabels: {
          save: 'Salva dati POD orari',
          add: 'Aggiungi POD orario',
        },
        render: () => (
          <DiagnosisPodTimetablesStep
            ref={stepRefs[7]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Indicatori',
        actions: ['add'],
        actionLabels: {
          add: 'Aggiungi indicatore',
        },
        render: () => (
          <DiagnosisIndicatorsStep
            ref={stepRefs[8]}
            diagnosisId={diagnosisId}
            diagnosis={memoizedDiagnosis}
          />
        ),
      },
      {
        label: 'Usi energetici',
        actions: ['add', 'save'],
        actionLabels: { add: 'Aggiungi uso energetico' },
        render: () => <DiagnosisEnergyUseStep ref={stepRefs[9]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Censimenti',
        actions: ['add'],
        actionLabels: { add: 'Aggiungi censimento' },
        render: () => <DiagnosisCensimentiStep ref={stepRefs[10]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Monitoraggio ENEA',
        actions: [],
        actionLabels: { save: 'Salva Monitoraggio ENEA' },
        render: () => <DiagnosisMonitorEneaStep ref={stepRefs[11]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Interventi',
        actions: ['add'],
        actionLabels: { add: 'Aggiungi intervento' },
        render: () => <DiagnosisInterventionsStep ref={stepRefs[12]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Gerarchia',
        actions: [],
        render: () => <DiagnosisGerarchieStep ref={stepRefs[13]} diagnosisId={diagnosisId} />,
      },
      {
        label: 'Chiudi ed esporta',
        actions: [],
        render: () => <DiagnosisCloseExportStep ref={stepRefs[14]} diagnosisId={diagnosisId} />,
      },
    ].map(step => (isReadOnly ? { ...step, actions: [] as StepConfig['actions'] } : step)) as StepConfig[];
  }, [diagnosisId, memoizedDiagnosis, memoizedDiagnosisSite, memoizedDiagnosisCustomer, stepRefs, isReadOnly]);

  React.useEffect(() => {
    if (steps.length === 0) return;
    if (searchParams.get('step') !== 'close-export') return;
    const closeExportIndex = STEP_LABELS.indexOf('Chiudi ed esporta');
    if (closeExportIndex >= 0 && closeExportIndex < steps.length && activeStep !== closeExportIndex) {
      setActiveStep(closeExportIndex);
    }
  }, [searchParams, steps.length, activeStep]);

  const handleRequestSave = async (stepIndex: number, force = false) => {
    const ref = stepRefs[stepIndex]?.current;
    if (!ref?.save) {
      return { success: true };
    }
    const result = await ref.save(force);
    if (result.requiresConfirmation && !force) {
      setConfirmState({ open: true, stepIndex, targetStep: null });
    }
    return result;
  };

  // Funzione centralizzata per cambiare step con salvataggio automatico
  const setActiveStepWithSave = async (newStep: number) => {
    if (isSaving || activeStep === newStep) {
      return;
    }

    if (!isReadOnly) {
      const result = await handleRequestSave(activeStep);

      if (result.requiresConfirmation) {
        setConfirmState(prev => ({ ...prev, targetStep: newStep }));
        return;
      }

      if (!result.success) {
        return;
      }
    }

    setActiveStep(newStep);
  };

  const handleSave = async () => {
    if (isSaving || !steps[activeStep]) {
      return;
    }
    setIsSaving(true);
    try {
      await handleRequestSave(activeStep);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = async () => {
    const ref = stepRefs[activeStep]?.current;
    await ref?.add?.();
  };

  const handleConfirmProceed = async () => {
    if (confirmState.stepIndex === null) {
      setConfirmState({ open: false, stepIndex: null, targetStep: null });
      return;
    }
    setIsSaving(true);
    try {
      const result = await handleRequestSave(confirmState.stepIndex, true);
      // Dopo il salvataggio forzato riuscito, cambia allo step target se presente
      // (se il salvataggio fallisce si resta sullo step per non perdere le modifiche)
      if (result.success && confirmState.targetStep !== null) {
        setActiveStep(confirmState.targetStep);
      }
    } finally {
      setConfirmState({ open: false, stepIndex: null, targetStep: null });
      setIsSaving(false);
    }
  };

  const handleBack = async () => {
    const newStep = Math.max(activeStep - 1, 0);
    await setActiveStepWithSave(newStep);
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      if (!isReadOnly) {
        setCloseConfirmOpen(true);
      }
      return;
    }
    const newStep = Math.min(activeStep + 1, steps.length - 1);
    await setActiveStepWithSave(newStep);
  };

  const handleExport = async () => {
    if (!diagnosisId || isExporting) return;
    setIsExporting(true);
    try {
      const { blob, filename } = await diagnosisCloseExportApi.generateReport(diagnosisId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report esportato con successo');
    } catch (error: unknown) {
      let message = "Errore durante l'export della diagnosi";
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          message = "L'export sta impiegando troppo tempo. Riprova più tardi.";
        } else if (!error.response) {
          message = 'Impossibile contattare il servizio di export';
        } else if (error.response.status === 401) {
          message = "Sessione non autorizzata per l'export. Effettua di nuovo il login.";
        } else {
          // con responseType 'blob' anche il corpo dell'errore arriva come Blob (ProblemDetails JSON)
          try {
            const body =
              error.response.data instanceof Blob
                ? JSON.parse(await error.response.data.text())
                : error.response.data;
            if (typeof body?.detail === 'string' && body.detail) message = body.detail;
          } catch {
            /* mantiene il messaggio di default */
          }
        }
      }
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleConfirmCloseDiagnosis = async () => {
    if (!diagnosisId) {
      setCloseConfirmOpen(false);
      return;
    }
    setIsClosingDiagnosis(true);
    try {
      await diagnosisCloseExportApi.closeDiagnosis(diagnosisId);
      await Promise.all([
        queryClient.invalidateQueries('diagnosis'),
        queryClient.invalidateQueries(DIAGNOSIS_ALL_QUERY_KEY),
        queryClient.invalidateQueries(['diagnosis', diagnosisId]),
      ]);
      toast.success('Diagnosi marcata come chiusa');
    } catch {
      toast.error('Errore durante la chiusura della diagnosi');
    } finally {
      setIsClosingDiagnosis(false);
      setCloseConfirmOpen(false);
    }
  };

  const currentStep = steps[activeStep];

  // Get actions from step handle if available, otherwise use step config
  const availableActions = currentStep?.actions ?? [];
  const canSave = availableActions.includes('save');
  const canAdd = availableActions.includes('add');

  if (!diagnosisId) {
    return (
      <PageLayout header={<Typography variant="h5">Diagnosi</Typography>}>
        <Alert severity="error">Identificativo diagnosi mancante.</Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <Stack direction="row" spacing={2} alignItems="center">
          <AssignmentIcon fontSize="large" />
          <Stack>
            <Typography variant="h5">{diagnosis?.table_code_index}</Typography>
          </Stack>
        </Stack>
      }
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <ActionButton
            variant="outlined"
            icon={<NavigateBeforeIcon />}
            tooltip="Indietro"
            onClick={handleBack}
            disabled={activeStep === 0}
          />
          {(canSave || canAdd) && (
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 1, borderColor: 'divider' }}
            />
          )}

          {canSave && (
            <>
              <ActionButton
                icon={<SaveIcon />}
                tooltip={currentStep?.actionLabels?.save ?? 'Salva'}
                onClick={handleSave}
                loading={isSaving || isLoading}
                disabled={isLoading || isSaving || !currentStep}
              />
              {canAdd && (
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 0.5, borderColor: 'divider' }}
                />
              )}
            </>
          )}

          {canAdd && (
            <ActionButton
              icon={<AddIcon />}
              tooltip={currentStep?.actionLabels?.add ?? 'Aggiungi'}
              onClick={handleAdd}
              disabled={isLoading}
            />
          )}

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 1, borderColor: 'divider' }}
          />

          {activeStep === steps.length - 1 && !isReadOnly && (
            <>
              <ActionButton
                icon={<FileDownloadIcon />}
                tooltip={isExporting ? 'Export in corso…' : 'Esporta'}
                onClick={handleExport}
                loading={isExporting}
                disabled={isClosingDiagnosis}
              />
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ mx: 0.5, borderColor: 'divider' }}
              />
            </>
          )}

          <ActionButton
            icon={activeStep === steps.length - 1 && !isReadOnly ? <CheckIcon /> : <NavigateNextIcon />}
            tooltip={
              activeStep === steps.length - 1
                ? isReadOnly
                  ? 'Fine'
                  : 'Chiudi diagnosi'
                : 'Avanti'
            }
            onClick={handleNext}
            loading={isClosingDiagnosis}
            disabled={isClosingDiagnosis || (activeStep === steps.length - 1 && isReadOnly)}
          />
        </Stack>
      }
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 6,
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress size={80} />
        </Box>
      ) : hasError ? (
        <Alert severity="error">
          Errore nel caricamento dei dati della diagnosi. Riprova più tardi.
        </Alert>
      ) : (
        <DiagnosisPermissionProvider readOnly={isReadOnly}>
          <Box
            sx={{
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              maxWidth: '100%',
            }}
          >
            {isReadOnly && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Stai visualizzando questa diagnosi in modalità sola lettura. Non puoi modificare o
                salvare i dati.
              </Alert>
            )}
            <Stepper activeStep={activeStep} alternativeLabel nonLinear sx={{ mb: 3 }}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepButton onClick={() => setActiveStepWithSave(index)}>{step.label}</StepButton>
                </Step>
              ))}
            </Stepper>
            <Divider variant="middle"></Divider>

            <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1 }}>
              {steps.map((step, index) => {
                const isActive = index === activeStep;
                return (
                  <Box key={step.label} sx={{ display: isActive ? 'block' : 'none' }}>
                    {isActive && step.render()}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DiagnosisPermissionProvider>
      )}

      <ConfirmationDialog
        open={confirmState.open}
        title="Campi obbligatori mancanti"
        description="Alcuni campi obbligatori non sono compilati correttamente. Vuoi procedere comunque con il salvataggio?"
        confirmLabel="Procedi"
        cancelLabel="Annulla"
        onConfirm={handleConfirmProceed}
        onCancel={() => setConfirmState({ open: false, stepIndex: null, targetStep: null })}
      />
      <ConfirmationDialog
        open={closeConfirmOpen}
        title="Conferma chiusura diagnosi"
        description="Confermando, la diagnosi verrà marcata come chiusa ma resterà modificabile."
        confirmLabel="Chiudi diagnosi"
        cancelLabel="Annulla"
        onConfirm={handleConfirmCloseDiagnosis}
        onCancel={() => setCloseConfirmOpen(false)}
        disableConfirm={isClosingDiagnosis}
      />
    </PageLayout>
  );
});

export default DiagnosisDetailPage;
